"use client"

import { useState, useEffect, useMemo } from "react"
import axios from "axios"
import { useAuth } from "../context/AuthContext"
import config from "../config"
import { useNavigate } from "react-router-dom"
import PaymentModal from "./PaymentModal"
import { Line, Bar, Doughnut } from 'react-chartjs-2'
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Title, Tooltip, Legend, Filler } from 'chart.js'
import { motion, AnimatePresence } from 'framer-motion'
import { format, subDays, isToday, isTomorrow } from 'date-fns'
import { TrendingUp, TrendingDown, Users, Calendar, DollarSign, Activity, Bell, Search, RefreshCw, ChevronUp, ChevronDown, Clock, MapPin, Target, Zap, BarChart3, PieChart } from 'lucide-react'

const Dashboard = () => {
  const { user, loading, isLoggedIn } = useAuth()
  const navigate = useNavigate()

  // Register Chart.js components
  ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Title, Tooltip, Legend, Filler)

  // State variables
  const [showModal, setShowModal] = useState(false)
  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const [bids, setBids] = useState([])
  const [events, setEvents] = useState({}) // Store events by ID
  const [invitations, setInvitations] = useState([])
  const [activeBid, setActiveBid] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [eventAttendees, setEventAttendees] = useState({})
  const [loadingEvents, setLoadingEvents] = useState({})
  const [subscription, setSubscription] = useState(null)
  const [acceptedInvitations, setAcceptedInvitations] = useState([])
  const [eventsView, setEventsView] = useState('all') // 'all', 'organizing', or 'attending'
  const [organizedEvents, setOrganizedEvents] = useState([]);
  const [attendingEvents, setAttendingEvents] = useState([]);
  const [eventsToDisplay, setEventsToDisplay] = useState([]);
  
  // Advanced dashboard states
  const [analytics, setAnalytics] = useState({
    totalRevenue: 0,
    totalEvents: 0,
    totalAttendees: 0,
    growthRate: 0,
    monthlyTrends: [],
    eventTypes: [],
    topPerformingEvents: [],
    recentActivity: []
  })
  const [timeRange, setTimeRange] = useState('7d') // '7d', '30d', '90d', '1y'
  const [searchQuery, setSearchQuery] = useState('')
  const [showNotifications, setShowNotifications] = useState(false)
  const [notifications, setNotifications] = useState([])
  const [refreshing, setRefreshing] = useState(false)
  const [expandedSections, setExpandedSections] = useState({
    analytics: true,
    events: true,
    activity: false
  })
  const [expandedCards, setExpandedCards] = useState({
    revenueTrends: true,
    eventTypes: true,
    eventsCreated: true,
    attendeeGrowth: true
  })

  // Update events to display when eventsView or events change
  useEffect(() => {
    if (eventsView === 'all') {
      setEventsToDisplay(Object.values(events));
    } else if (eventsView === 'organizing') {
      setEventsToDisplay(organizedEvents);
    } else if (eventsView === 'attending') {
      setEventsToDisplay(attendingEvents);
    }
  }, [eventsView, events, organizedEvents, attendingEvents]);

  // Fetch analytics data
  const fetchAnalytics = async () => {
    try {
      const allEvents = Object.values(events)
      const allAttendees = Object.values(eventAttendees)
      
      // Calculate real revenue from actual events and attendees
      const totalRevenue = allEvents.reduce((sum, event) => {
        const attendeeCount = eventAttendees[event.id] || 0
        const ticketPrice = event.ticket_price || 0
        return sum + (attendeeCount * ticketPrice)
      }, 0)
      
      const totalAttendees = allAttendees.reduce((sum, count) => sum + count, 0)
      const totalEvents = allEvents.length
      
      // Calculate real growth rate (compare with previous period)
      const thirtyDaysAgo = subDays(new Date(), 30)
      const recentEvents = allEvents.filter(event => new Date(event.created_at || event.date) > thirtyDaysAgo)
      const olderEvents = allEvents.filter(event => new Date(event.created_at || event.date) <= thirtyDaysAgo)
      const growthRate = olderEvents.length > 0 ? 
        ((recentEvents.length - olderEvents.length) / olderEvents.length) * 100 : 0
      
      // Generate real monthly trends from actual event data
      const monthlyTrends = []
      for (let i = 6; i >= 0; i--) {
        const monthStart = subDays(new Date(), (i + 1) * 30)
        const monthEnd = subDays(new Date(), i * 30)
        
        const monthEvents = allEvents.filter(event => {
          const eventDate = new Date(event.created_at || event.date)
          return eventDate >= monthStart && eventDate < monthEnd
        })
        
        const monthRevenue = monthEvents.reduce((sum, event) => {
          const attendeeCount = eventAttendees[event.id] || 0
          const ticketPrice = event.ticket_price || 0
          return sum + (attendeeCount * ticketPrice)
        }, 0)
        
        const monthAttendees = monthEvents.reduce((sum, event) => sum + (eventAttendees[event.id] || 0), 0)
        
        monthlyTrends.push({
          month: format(monthStart, 'MMM'),
          revenue: monthRevenue,
          events: monthEvents.length,
          attendees: monthAttendees
        })
      }
      
      // Calculate real event type distribution from actual events
      const eventTypeCount = {}
      allEvents.forEach(event => {
        // Try to determine event type from title/description or use a default
        let eventType = 'Other'
        const title = (event.title || '').toLowerCase()
        const description = (event.description || '').toLowerCase()
        
        if (title.includes('conference') || description.includes('conference')) {
          eventType = 'Conference'
        } else if (title.includes('workshop') || description.includes('workshop')) {
          eventType = 'Workshop'
        } else if (title.includes('meetup') || description.includes('meetup') || title.includes('meet')) {
          eventType = 'Meetup'
        } else if (title.includes('social') || description.includes('social') || title.includes('party')) {
          eventType = 'Social'
        } else if (title.includes('seminar') || description.includes('seminar')) {
          eventType = 'Conference'
        } else if (title.includes('training') || description.includes('training')) {
          eventType = 'Workshop'
        }
        
        eventTypeCount[eventType] = (eventTypeCount[eventType] || 0) + 1
      })
      
      const totalEventTypes = Object.values(eventTypeCount).reduce((sum, count) => sum + count, 0)
      const eventTypes = Object.entries(eventTypeCount).map(([name, value]) => ({
        name,
        value: Math.round((value / totalEventTypes) * 100),
        color: name === 'Conference' ? '#8B5CF6' : 
               name === 'Workshop' ? '#10B981' : 
               name === 'Meetup' ? '#F59E0B' : 
               name === 'Social' ? '#EF4444' : '#6B7280'
      }))
      
      // Calculate real conversion rate (attendees vs potential attendees)
      const totalCapacity = allEvents.reduce((sum, event) => sum + (event.max_attendees || 100), 0)
      const conversionRate = totalCapacity > 0 ? (totalAttendees / totalCapacity) * 100 : 0
      
      // Create real activity feed from recent events
      const recentActivity = []
      
      // Add recent event creations
      const recentCreatedEvents = allEvents
        .filter(event => new Date(event.created_at || event.date) > subDays(new Date(), 7))
        .sort((a, b) => new Date(b.created_at || b.date) - new Date(a.created_at || a.date))
        .slice(0, 3)
      
      recentCreatedEvents.forEach(event => {
        recentActivity.push({
          type: 'event_created',
          message: `New event "${event.title || 'Untitled Event'}" created`,
          time: format(new Date(event.created_at || event.date), 'MMM d, yyyy'),
          icon: Calendar
        })
      })
      
      // Add recent registrations (if we have attendee data)
      const recentRegistrations = allEvents
        .filter(event => (eventAttendees[event.id] || 0) > 0)
        .slice(0, 2)
      
      recentRegistrations.forEach(event => {
        const attendeeCount = eventAttendees[event.id] || 0
        recentActivity.push({
          type: 'registration',
          message: `${attendeeCount} registrations for "${event.title || 'Event'}"`,
          time: format(new Date(event.date || event.created_at), 'MMM d, yyyy'),
          icon: Users
        })
      })
      
      // Add revenue milestones
      if (totalRevenue > 0) {
        recentActivity.push({
          type: 'payment',
          message: `Total revenue: KES ${totalRevenue.toLocaleString()}`,
          time: 'Current',
          icon: DollarSign
        })
      }
      
      // Sort activity by time
      recentActivity.sort((a, b) => {
        if (a.time === 'Current') return -1
        if (b.time === 'Current') return 1
        return new Date(b.time) - new Date(a.time)
      })
      
      setAnalytics({
        totalRevenue,
        totalEvents,
        totalAttendees,
        growthRate: Math.round(growthRate * 10) / 10,
        monthlyTrends,
        eventTypes,
        topPerformingEvents: allEvents
          .map(event => ({
            ...event,
            revenue: (eventAttendees[event.id] || 0) * (event.ticket_price || 0),
            attendees: eventAttendees[event.id] || 0
          }))
          .sort((a, b) => b.revenue - a.revenue)
          .slice(0, 5),
        recentActivity: recentActivity.slice(0, 6)
      })
    } catch (error) {
      console.error('Error fetching analytics:', error)
    }
  }

  // Fetch notifications
  const fetchNotifications = async () => {
    try {
      const allEvents = Object.values(events)
      const notifications = []
      
      // Add event reminders for upcoming events
      const upcomingEvents = allEvents
        .filter(event => {
          const eventDate = new Date(event.date)
          const now = new Date()
          const hoursUntilEvent = (eventDate - now) / (1000 * 60 * 60)
          return hoursUntilEvent > 0 && hoursUntilEvent <= 48 // Events in next 48 hours
        })
        .sort((a, b) => new Date(a.date) - new Date(b.date))
      
      upcomingEvents.slice(0, 2).forEach(event => {
        const eventDate = new Date(event.date)
        const hoursUntilEvent = (eventDate - new Date()) / (1000 * 60 * 60)
        
        let timeText = ''
        if (hoursUntilEvent <= 2) {
          timeText = 'Starts soon'
        } else if (hoursUntilEvent <= 24) {
          timeText = `${Math.floor(hoursUntilEvent)} hours`
        } else {
          timeText = `${Math.floor(hoursUntilEvent / 24)} days`
        }
        
        notifications.push({
          id: `event-${event.id}`,
          title: 'Event Reminder',
          message: `Your event "${event.title || 'Untitled Event'}" ${timeText}`,
          read: false,
          time: timeText
        })
      })
      
      // Add new registration notifications
      const eventsWithRegistrations = allEvents
        .filter(event => (eventAttendees[event.id] || 0) > 0)
        .sort((a, b) => (eventAttendees[b.id] || 0) - (eventAttendees[a.id] || 0))
      
      eventsWithRegistrations.slice(0, 2).forEach(event => {
        const attendeeCount = eventAttendees[event.id] || 0
        notifications.push({
          id: `registration-${event.id}`,
          title: 'New Registrations',
          message: `${attendeeCount} people registered for "${event.title || 'Event'}"`,
          read: false,
          time: 'Recent'
        })
      })
      
      // Add revenue notifications
      const totalRevenue = allEvents.reduce((sum, event) => {
        const attendeeCount = eventAttendees[event.id] || 0
        const ticketPrice = event.ticket_price || 0
        return sum + (attendeeCount * ticketPrice)
      }, 0)
      
      if (totalRevenue > 0) {
        notifications.push({
          id: 'revenue',
          title: 'Revenue Update',
          message: `Total earnings: KES ${totalRevenue.toLocaleString()}`,
          read: true,
          time: 'Today'
        })
      }
      
      // Add subscription notifications
      if (subscription) {
        const daysUntilExpiration = getDaysUntilExpiration()
        if (daysUntilExpiration !== null && daysUntilExpiration <= 7) {
          notifications.push({
            id: 'subscription',
            title: 'Subscription',
            message: `Your subscription expires in ${daysUntilExpiration} days`,
            read: false,
            time: 'Important'
          })
        }
      }
      
      setNotifications(notifications)
    } catch (error) {
      console.error('Error fetching notifications:', error)
    }
  }

  // Refresh all data
  const refreshData = async () => {
    setRefreshing(true)
    try {
      await Promise.all([fetchData(), fetchSubscription(), fetchAnalytics(), fetchNotifications()])
    } finally {
      setRefreshing(false)
    }
  }

  // Fetch subscription data
  const fetchSubscription = async () => {
    if (!user?.id) return;
    try {
      const response = await axios.get(`${config.API_BASE_URL}/subscriptions/user-subscription/`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
      });
      console.log("Subscription data:", response.data);
      setSubscription(response.data);
    } catch (error) {
      console.error("Error fetching subscription:", error);
    }
  };

  // Real revenue = sum of verified payments (matches the mobile app). The
  // attendees x ticket_price figure below is only an estimate/fallback.
  const [realRevenue, setRealRevenue] = useState(null);
  useEffect(() => {
    if (!user?.id) return;
    axios.get(`${config.API_BASE_URL}/events/organizer/summary/`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("access_token")}` },
    }).then((res) => {
      const byCur = res.data?.revenue_by_currency || {};
      const total = Object.values(byCur).reduce((s, v) => s + (Number(v) || 0), 0);
      setRealRevenue(total);
    }).catch(() => {});
  }, [user?.id]);

  // Pagination and filtering states
  const [bidsPage, setBidsPage] = useState(1)
  const [eventsPage, setEventsPage] = useState(1)
  const [bidsSearch, setBidsSearch] = useState("")
  const [eventsSearch, setEventsSearch] = useState("")
  const [bidsFilter, setBidsFilter] = useState("all")
  const [eventsFilter, setEventsFilter] = useState("all")

  const ITEMS_PER_PAGE = 5

  // Calculate days until subscription expires
  const getDaysUntilExpiration = () => {
    if (!subscription?.end_date) return null
    const expirationDate = new Date(subscription.end_date)
    const today = new Date()
    const diffTime = expirationDate - today
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return diffDays
  }

  // Function to handle invitation responses
  const handleAcceptInvitation = async (invitationId) => {
    try {
      const response = await axios.post(
        `${config.API_BASE_URL}/invitations/invite/respond/${invitationId}/`,
        { action: 'accept' },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            'Content-Type': 'application/json',
          },
        }
      )

      // Show success message
      // console.log('Invitation accepted successfully:', response.data);

      // Refresh the data
      fetchData();
    } catch (error) {
      // console.error("Error accepting invitation:", error.response?.data || error.message);
      // Add user feedback here if needed
    }
  }

  const handleDeclineInvitation = async (invitationId) => {
    try {
      const response = await axios.post(
        `${config.API_BASE_URL}/invitations/invite/respond/${invitationId}/`,
        { action: 'reject' },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            'Content-Type': 'application/json',
          },
        }
      )

      // Show success message
      // console.log('Invitation declined successfully:', response.data);

      // Refresh the data
      fetchData();
    } catch (error) {
      // console.error("Error declining invitation:", error.response?.data || error.message);
      // Add user feedback here if needed
    }
  }

  // Function to fetch event details by ID with better error handling
  const fetchEventDetails = async (eventId) => {
    if (!eventId) {
      // console.log('No event ID provided');
      return null;
    }

    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        // console.error('No access token found');
        return null;
      }

      const response = await axios.get(`${config.API_BASE_URL}/events/${eventId}/`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // Return just the basic event info we need
      if (response.data) {
        return {
          id: response.data.id,
          title: response.data.title,
          date: response.data.date,
          location: response.data.location,
          description: response.data.description
        };
      }
      return null;
    } catch (error) {
      // console.error(`Error fetching event ${eventId}:`, error.response?.data || error.message);
      return null;
    }
  };

  // Centralized data fetching function
  const fetchData = async () => {
    if (!user) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const headers = {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // For event organizers, fetch both created and invited events
      if (user.role === "event_organizer") {
        try {
          // Fetch created events and invitations in parallel
          const [eventsResponse, invitationsResponse] = await Promise.all([
            axios.get(`${config.API_BASE_URL}/events/all/`, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            }),
            axios.get(`${config.API_BASE_URL}/invitations/`, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              params: {
                status: 'accepted',  // Only fetch accepted invitations
                recipient: user.id   // Only get invitations for the current user
              }
            })
          ]);

          // Process events where user is an attendee from the invitations
          const acceptedInvitations = Array.isArray(invitationsResponse.data) ?
            invitationsResponse.data :
            (invitationsResponse.data?.results || []);

          // console.log('Accepted invitations:', acceptedInvitations);

          // Get all events from the events response
          const allEvents = Array.isArray(eventsResponse.data?.events) ?
            eventsResponse.data.events :
            (Array.isArray(eventsResponse.data?.results) ? eventsResponse.data.results : []);

          // console.log('All events:', allEvents);

          // Process attended events by matching invitation event IDs with events data
          const attendedEvents = acceptedInvitations
            .filter(invite => invite.event)
            .map(invite => {
              // Find the event in the already fetched events
              const event = allEvents.find(e => e.id === (invite.event.id || invite.event));
              if (!event) return null;

              return {
                ...event,
                isOrganizer: false,
                isAttendee: true,
                invitationStatus: invite.status || 'accepted',
                invitationId: invite.id
              };
            })
            .filter(event => event !== null);

          // console.log('User attended events:', attendedEvents);

          // Process created events
          const userEvents = allEvents
            .filter(event => event && event.created_by && (typeof event.created_by === 'object' ? event.created_by.id === user.id : event.created_by === user.id))
            .map(event => ({
              ...event,
              isOrganizer: true,
              isAttendee: false,
              created_at: event.created_at || new Date().toISOString()
            }));

          // console.log('User created events:', userEvents);

          // Process invited events (all invitations, not just accepted)
          const invitedEvents = acceptedInvitations
            .filter(invite => {
              const isRecipient = invite.recipient && (typeof invite.recipient === 'object' ? invite.recipient.id === user.id : invite.recipient === user.id);
              const isAccepted = invite.status === 'accepted' || invite.status === '1' || invite.status === 1;
              const hasEvent = !!invite.event;

              if (isRecipient && isAccepted && hasEvent) {
                // console.log('Processing accepted invitation:', invite);
                return true;
              }
              return false;
            })
            .map(invite => {
              const eventData = typeof invite.event === 'object' ?
                invite.event :
                (allEvents.find(e => e.id === invite.event) || {});

              return {
                ...eventData,
                isOrganizer: false,
                isAttendee: true,  // Make sure to mark as attendee
                invitation: invite,
                created_at: eventData.created_at || new Date().toISOString(),
                invitationStatus: 'accepted'
              };
            });

          // console.log('Invited events:', invitedEvents);

          // Combine and deduplicate events, giving priority to user-created events
          const combinedEvents = [...userEvents, ...invitedEvents];
          const uniqueEvents = Array.from(
            combinedEvents
              .filter(event => event && event.id) // Filter out any invalid events
              .reduce((map, event) => {
                // If event already exists, preserve the isOrganizer flag if set
                const existingEvent = map.get(event.id);
                if (existingEvent) {
                  // If the existing event is marked as organized, keep it that way
                  if (existingEvent.isOrganizer) {
                    return map;
                  }
                }
                map.set(event.id, event);
                return map;
              }, new Map())
              .values()
          );

          // console.log('Combined events before dedupe:', combinedEvents);
          // console.log('Unique events after dedupe:', uniqueEvents);

          // console.log('All unique events:', uniqueEvents);

          // Convert to object with IDs as keys
          const eventsMap = {};
          const organizedEvents = [];
          const attendingEvents = [];

          uniqueEvents.forEach(event => {
            if (event && event.id) {
              eventsMap[event.id] = event;

              // Categorize events
              if (event.isOrganizer) {
                organizedEvents.push(event);
              }
              if (event.isAttendee) {
                attendingEvents.push(event);
              }

              // console.log(`Event ${event.id} - isOrganizer: ${event.isOrganizer}, isAttendee: ${event.isAttendee}`);
            }
          });

          // console.log('Organized events:', organizedEvents);
          // console.log('Attending events:', attendingEvents);

          // console.log('Final events map:', eventsMap);
          setEvents(eventsMap);
          setOrganizedEvents(organizedEvents);
          setAttendingEvents(attendingEvents);
        } catch (error) {
          console.error("Error fetching events:", error.response?.data || error.message);
          setEvents({});
        }
      }

      // For attendees and community members, fetch their invitations
      if (user.role === "event_attendee" || user.role === "community_member") {
        try {
          const fetchData = async () => {
            try {
              setIsLoading(true);

              // First, fetch all events
              const eventsResponse = await axios.get(`${config.API_BASE_URL}/events/all/`, {
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('access_token')}`
                }
              });

              // console.log('All events response:', eventsResponse.data);
              // console.log('All events:', eventsResponse.data.events);

              // The backend returns events in a nested 'events' property
              const allEvents = Array.isArray(eventsResponse.data?.events) ? eventsResponse.data.events : [];
              // console.log('All events:', allEvents);

              // Create a map of event ID to event for quick lookup
              const eventsMap = {};
              allEvents.forEach(event => {
                if (event && event.id) {
                  eventsMap[event.id] = event;
                }
              });

              // Then fetch invitations
              const invitationsResponse = await axios.get(`${config.API_BASE_URL}/invitations/`, {
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('access_token')}`
                }
              });

              const invitationsData = Array.isArray(invitationsResponse.data) ? invitationsResponse.data : [];
              // console.log('Raw invitations data:', invitationsData);

              // Process invitations
              const pendingInvitations = [];
              const acceptedInvitations = [];

              // Process each invitation
              invitationsData.forEach(invitation => {
                if (!invitation) return;

                // Log the raw invitation for debugging
                // console.log('Processing invitation:', invitation);

                // Handle different invitation formats
                const eventId = invitation.event?.id || (typeof invitation.event === 'string' ? invitation.event : null);
                if (!eventId) {
                  // console.warn('Skipping invitation with no valid event ID:', invitation);
                  return;
                }

                // Normalize status check
                const status = String(invitation.status || '').toLowerCase().trim();
                const isAccepted = status === 'accepted' ||
                  status === '1' ||
                  status === 'accepted_invitation';

                // Get the full event details from our events map
                const event = eventsMap[eventId];
                if (!event) {
                  // console.warn(`Event ${eventId} not found in events map`);
                  return;
                }

                const processedInvitation = {
                  ...invitation,
                  event: event,
                  // Ensure we have a consistent status
                  status: isAccepted ? 'accepted' : 'pending'
                };

                // console.log(`Processed invitation for event ${eventId} (${event.title}):`, {
                //   status: processedInvitation.status,
                //   isAccepted,
                //   eventDate: event.date
                // });

                if (isAccepted) {
                  acceptedInvitations.push(processedInvitation);
                } else {
                  pendingInvitations.push(processedInvitation);
                }
              });

              // console.log('Pending invitations:', pendingInvitations);
              // console.log('Accepted invitations:', acceptedInvitations);
              // console.log('Events map:', eventsMap);

              // Set states
              setInvitations(pendingInvitations);
              setAcceptedInvitations(acceptedInvitations);
              setEvents(eventsMap);

            } catch (error) {
              // console.error("Error in fetchData:", error);
              setInvitations([]);
              setAcceptedInvitations([]);
              setEvents({});
            } finally {
              setIsLoading(false);
            }
          }
          fetchData();
        } catch (error) {
          // console.error("Error fetching invitations:", error);
          setInvitations([]);
          setAcceptedInvitations([]);
          setEvents({});
        }
      }

      // Fetch subscription data
      await fetchSubscription();
    } catch (error) {
      // console.error("Error in fetchData:", error);
      setIsLoading(false);

      try {
        const response = await axios.get(`${config.API_BASE_URL}/subscriptions/user-subscription/`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        });
        setSubscription(response.data);
      } catch (subscriptionError) {
        // console.error("Error fetching subscription:", subscriptionError);
      }
    }
  }

  // Main useEffect for fetching data
  useEffect(() => {
    if (user) {
      fetchData()
      fetchSubscription()
      fetchAnalytics()
      fetchNotifications()
    }
  }, [user])

  // Fetch analytics when events or attendees change
  useEffect(() => {
    if (events && Object.keys(events).length > 0) {
      fetchAnalytics()
    }
  }, [events, eventAttendees])

  // Fetch event attendees when events load
  useEffect(() => {
    const fetchEventAttendees = async () => {
      const eventsList = Object.values(events);

      if (!user || !eventsList || eventsList.length === 0) {
        // console.log("No events to process or user not available");
        setEventAttendees({});
        setLoadingEvents({});
        setIsLoading(false);
        return;
      }

      // console.log(`Processing ${eventsList.length} events for attendee count...`, events);

      // Initialize loading states
      const initialLoading = {};
      eventsList.forEach(event => {
        const eventId = event.id || event._id; // Try both id and _id
        if (eventId) {
          initialLoading[eventId] = true;
          // console.log(`Processing event ID: ${eventId}, Title: ${event.title || 'N/A'}, Date: ${event.date || 'N/A'}`);
        } else {
          // console.warn('Event missing ID:', event);
        }
      });
      setLoadingEvents(initialLoading);

      try {
        // Initialize with existing data if available
        const initialAttendees = {};
        eventsList.forEach(event => {
          initialAttendees[event.id] = event.attendees_count !== undefined ?
            event.attendees_count :
            (eventAttendees[event.id] !== undefined ? eventAttendees[event.id] : 0);
        });
        setEventAttendees(initialAttendees);
        const token = localStorage.getItem("access_token");
        const newEventAttendees = {};

        for (const event of eventsList) {
          // console.log(`\nProcessing event ID: ${event.id}, Title: ${event.title}`);

          try {
            let attendeeCount = 0;

            if (event.attendees_count) {
              // console.log(`Using pre-fetched attendees_count: ${event.attendees_count}`);
              attendeeCount = event.attendees_count;
            } else {
              // console.log(`Fetching invitations for event ${event.id}...`);
              const invitationsResponse = await axios.get(
                `${config.API_BASE_URL}/invitations/?event_id=${event.id}`,
                { headers: { Authorization: `Bearer ${token}` } }
              );

              // console.log(`API Response for event ${event.id}:`, invitationsResponse.data);

              // Handle different response formats
              let invitations = [];
              if (Array.isArray(invitationsResponse.data)) {
                invitations = invitationsResponse.data;
              } else if (invitationsResponse.data?.results) {
                invitations = invitationsResponse.data.results;
              } else if (invitationsResponse.data?.invitations) {
                invitations = invitationsResponse.data.invitations;
              } else if (invitationsResponse.data && typeof invitationsResponse.data === 'object') {
                invitations = [invitationsResponse.data];
              }

              if (Array.isArray(invitations)) {
                const acceptedInvitations = invitations.filter(invitation =>
                  invitation && (
                    invitation.status === "accepted" ||
                    invitation.status === "ACCEPTED" ||
                    invitation.status === 1 ||
                    invitation.status === "1" ||
                    (invitation.status &&
                      typeof invitation.status === 'object' &&
                      (invitation.status.name === 'accepted' ||
                        invitation.status.name === 'ACCEPTED'))
                  )
                );

                attendeeCount = acceptedInvitations.length;
                // console.log(`Event ${event.id} has ${attendeeCount} accepted invitations`);
              }
            }

            newEventAttendees[event.id] = attendeeCount;

          } catch (error) {
            // console.error(`Error processing event ${event.id}:`, error);
            newEventAttendees[event.id] = 0; // Default to 0 on error
          }
        }

        // console.log('Updated event attendees:', newEventAttendees);
        setEventAttendees(newEventAttendees);
      } catch (error) {
        // console.error("Error in fetchEventAttendees:", error);
        // Keep the initial attendee counts on error
      } finally {
        // Mark all events as done loading
        setLoadingEvents({});
        setIsLoading(false);
      }
    };

    fetchEventAttendees();
  }, [user, events]);

  // Calculate total attendees across all events
  const totalAttendees = useMemo(() => {
    return Object.values(eventAttendees).reduce((sum, count) => sum + count, 0);
  }, [eventAttendees]);

  // Computed values from database data
  const acceptedBids = bids.filter((bid) => bid.status === "accepted")
  const rejectedBids = bids.filter((bid) => bid.status === "rejected")
  const pendingBids = bids.filter((bid) => bid.status === "pending")
  const totalRevenue = acceptedBids.reduce((sum, bid) => sum + (bid.price || 0)
    , 0)

  // Categorize events for attendees/community members
  const now = new Date();

  // Get accepted events (both upcoming and past)
  const acceptedEvents = useMemo(() => {
    // console.log('Calculating acceptedEvents...');
    // console.log('Events:', events);
    // console.log('Event attendees:', eventAttendees);

    // If we have acceptedInvitations in state, use those
    if (acceptedInvitations?.length > 0) {
      // console.log('Using acceptedInvitations from state:', acceptedInvitations);
      return acceptedInvitations.map(invite => {
        const event = events[invite.event] || invite.event;
        if (!event) return null;
        return {
          ...event,
          invitation: invite,
          attendees_count: eventAttendees[event.id] || event.attendees_count || 0
        };
      }).filter(Boolean);
    }

    // Fallback: look through all events and check their attendee count
    const accepted = [];
    Object.values(events).forEach(event => {
      if (event && eventAttendees[event.id] > 0) {
        accepted.push({
          ...event,
          attendees_count: eventAttendees[event.id] || 0,
          hasAcceptedInvitation: true
        });
      }
    });

    // console.log('Accepted events from attendees count:', accepted);
    return accepted;
  }, [events, eventAttendees, acceptedInvitations]);

  // State for pending invitations
  const [pendingInvitations, setPendingInvitations] = useState([]);

  // Fetch pending invitations
  useEffect(() => {
    const fetchPendingInvitations = async () => {
      if (!user?.id) return;

      try {
        const token = localStorage.getItem('access_token');

        // Fetch all invitations
        const response = await axios.get(`${config.API_BASE_URL}/invitations/`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        // Get invitations where user is either sender or recipient and status is pending
        const userInvitations = (response.data?.results || response.data || []).filter(invite => {
          if (!invite) return false;

          const isUserInvolved =
            String(invite.sender_id) === String(user.id) ||
            String(invite.recipient_id) === String(user.id);

          const status = String(invite.status || '').toLowerCase();
          const isPending = status === 'pending' || status === '0' || status === '';

          return isUserInvolved && isPending;
        });

        // console.log('Found pending invitations:', userInvitations);

        // Get unique event IDs from invitations
        const eventIds = [...new Set(userInvitations.map(inv => inv.event))];

        // Fetch event details for each invitation
        const eventsResponse = await Promise.all(
          eventIds.map(eventId =>
            axios.get(`${config.API_BASE_URL}/events/${eventId}/`, {
              headers: { 'Authorization': `Bearer ${token}` }
            }).catch(err => {
              // console.error(`Error fetching event ${eventId}:`, err);
              return null;
            })
          )
        );

        // Combine event data with invitation data
        const pendingEvents = eventsResponse
          .filter(res => res && res.data)
          .map(res => {
            const event = res.data;
            const invitation = userInvitations.find(inv => inv.event === event.id);
            return {
              ...event,
              isPending: true,
              invitation,
              attendees_count: eventAttendees[event.id] || event.attendees_count || 0
            };
          });

        // console.log('Processed pending events:', pendingEvents);
        setPendingInvitations(pendingEvents);

      } catch (error) {
        // console.error('Error fetching pending invitations:', error);
        setPendingInvitations([]);
      }
    };

    fetchPendingInvitations();
  }, [user?.id, eventAttendees]);

  // Event calculations
  const today = new Date();
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const todayEnd = new Date(todayStart.getTime() + 24 * 60 * 60 * 1000);

  // Filter events based on user role and status
  const { upcomingEvents, pastEvents } = useMemo(() => {
    const upcoming = [];
    const past = [];
    const currentTime = new Date();

    // console.log('Filtering events. User role:', user?.role);
    // console.log('Total events:', Object.values(events).length);
    // console.log('Accepted events:', acceptedEvents);
    // console.log('Event attendees:', eventAttendees);

    // For all users, show events based on their role and permissions
    const eventsToProcess = user?.role === 'organizer'
      ? Object.values(events)
      : acceptedEvents.length > 0
        ? acceptedEvents
        : [];

    // console.log('Events to process:', eventsToProcess);

    eventsToProcess.forEach(event => {
      if (!event) {
        // console.warn('Skipping null/undefined event');
        return;
      }

      const eventDate = event.date ? new Date(event.date) : null;
      if (!eventDate) {
        // console.warn(`Event ${event.id} has no valid date:`, event.date);
        return;
      }

      // Check if user has any invitation (accepted or pending) or is the organizer
      const hasAnyInvitation = eventAttendees[event.id] > 0 ||
        event.hasAcceptedInvitation ||
        (event.invitation && (
          event.invitation.status === 'accepted' ||
          event.invitation.status === '1' ||
          event.invitation.status === 'pending' ||
          event.invitation.status === '0' ||
          (event.invitation.recipient === user?.id ||
            event.invitation.sender === user?.id)
        ));

      // console.log(`Processing event ${event.id} (${event.title}):`, {
      //   eventDate,
      //   now: currentTime,
      //   isUpcoming: eventDate >= currentTime,
      //   attendeesCount: event.attendees_count,
      //   hasAnyInvitation,
      //   invitation: event.invitation
      // });

      // Include events with any invitation status or for organizers
      if (hasAnyInvitation || user?.role === 'organizer') {
        if (eventDate >= currentTime) {
          // console.log(`Adding to upcoming: ${event.id} - ${event.title}`);
          upcoming.push(event);
        } else {
          // console.log(`Adding to past: ${event.id} - ${event.title}`);
          past.push(event);
        }
      }
    });

    // console.log('Filtered events:', { upcoming, past });

    // Sort upcoming by date (closest first)
    upcoming.sort((a, b) => new Date(a.date) - new Date(b.date));
    // Sort past by date (most recent first)
    past.sort((a, b) => new Date(b.date) - new Date(a.date));

    return { upcomingEvents: upcoming, pastEvents: past };
  }, [events, acceptedEvents, user?.role, now]);

  // Get role-specific quick action
  const getQuickAction = () => {
    switch (user?.role) {
      case "service_provider":
        return {
          label: "Browse Open Bids",
          icon: "M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z",
          action: () => navigate("/bids"),
        }
      case "event_organizer":
        return {
          label: "Create Event",
          icon: "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z",
          action: () => navigate("/my-events"),
        }
      case "event_attendee":
      case "community_member":
        return {
          label: "Discover Events",
          icon: "M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z",
          action: () => navigate("/all-events"),
        }
      default:
        return {
          label: "Quick Action",
          icon: "M12 6v6m0 0v6m0-6h6m-6 0H6",
          action: () => console.log("Default action"),
        }
    }
  }

  // Get role-specific statistics from actual database data
  const getRoleStats = () => {
    switch (user?.role) {
      case "service_provider":
        return [
          {
            title: "Open Bids",
            value: pendingBids.length,
            description: "Available opportunities",
            icon: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
            color: "text-blue-900",
            bgColor: "bg-blue-50",
          },
          {
            title: "Won Projects",
            value: acceptedBids.length,
            description: `${bids.length > 0 ? Math.round((acceptedBids.length / bids.length) * 100) : 0}% success rate`,
            icon: "M5 13l4 4L19 7",
            color: "text-green-700",
            bgColor: "bg-green-50",
          },
          {
            title: "Total Earnings",
            value: `KSh ${totalRevenue.toLocaleString()}`,
            description: "From completed projects",
            icon: "M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1",
            color: "text-yellow-800",
            bgColor: "bg-yellow-50",
          },
          {
            title: "Active Bids",
            value: bids.length,
            description: "Total submissions",
            icon: "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z",
            color: "text-purple-700",
            bgColor: "bg-purple-50",
          },
        ]
      case "event_organizer":
        // Calculate organizing vs attending events
        const organizingEvents = Object.values(events).filter(e => e.isOrganizer === true);
        const attendingEvents = Object.values(events).filter(e => e.isOrganizer === false);

        // Calculate total attendees across events the user is organizing
        const totalAttendeesCount = Object.entries(eventAttendees).reduce((sum, [eventId, count]) => {
          const event = events[eventId];
          // Only count attendees for events where the user is the organizer
          if (event && event.isOrganizer) {
            return sum + (count || 0);
          }
          return sum;
        }, 0);

        return [
          {
            title: "Organizing",
            value: organizingEvents.length,
            description: "Events you're organizing",
            icon: "M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2H5a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10",
            color: "text-blue-900",
            bgColor: "bg-blue-50",
          },
          {
            title: "Attending",
            value: attendingEvents.length,
            description: "Events you're invited to",
            icon: "M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z",
            color: "text-green-700",
            bgColor: "bg-green-50",
          },
          {
            title: "Total Attendees",
            value: totalAttendeesCount,
            description: "Across all events",
            icon: "M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z",
            color: "text-purple-700",
            bgColor: "bg-purple-50",
          },
          {
            title: "Active Events",
            value: activeEvents.length,
            description: "Currently running",
            icon: "M13 10V3L4 14h7v7l9-11h-7z",
            color: "text-orange-700",
            bgColor: "bg-orange-50",
          },
        ]
      case "event_attendee":
      case "community_member":
        return [
          {
            title: "Events Attended",
            value: pastEvents.length,
            description: "Past events you attended",
            icon: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4",
            color: "text-purple-700",
            bgColor: "bg-purple-50",
          },
          {
            title: "Upcoming Events",
            value: upcomingEvents.length,
            description: "Events you're attending",
            icon: "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z",
            color: "text-blue-900",
            bgColor: "bg-blue-50",
          },
          {
            title: "Pending Invites",
            value: pendingInvitations.length,
            description: "Needs your response",
            icon: "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z",
            color: "text-yellow-800",
            bgColor: "bg-yellow-50",
          },
          {
            title: "Total Events",
            value: pastEvents.length + upcomingEvents.length,
            description: "All your events",
            icon: "M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10",
            color: "text-indigo-700",
            bgColor: "bg-indigo-50",
          },
        ]
      default:
        return []
    }
  }

  // Filtered and paginated bids
  const filteredBids = useMemo(() => {
    let filtered = bids.filter(
      (bid) =>
        bid.title?.toLowerCase().includes(bidsSearch.toLowerCase()) ||
        (bid.event?.title || "").toLowerCase().includes(bidsSearch.toLowerCase()),
    )
    if (bidsFilter !== "all") {
      filtered = filtered.filter((bid) => bid.status === bidsFilter)
    }
    return filtered
  }, [bids, bidsSearch, bidsFilter])

  const paginatedBids = useMemo(() => {
    const startIndex = (bidsPage - 1) * ITEMS_PER_PAGE
    return filteredBids.slice(startIndex, startIndex + ITEMS_PER_PAGE)
  }, [filteredBids, bidsPage])

  const totalBidsPages = Math.ceil(filteredBids.length / ITEMS_PER_PAGE)

  // Get all events as an array for filtering
  const eventsArray = useMemo(() => {
    return Object.values(events).filter(event => event != null);
  }, [events]);

  // Get active events (happening today)
  const activeEvents = useMemo(() => {
    return eventsArray.filter((event) => {
      if (!event?.date) return false;
      const eventDate = new Date(event.date);
      return eventDate >= todayStart && eventDate < todayEnd;
    });
  }, [eventsArray, todayStart, todayEnd]);

  // Get completed events
  const completedEvents = useMemo(() => {
    return eventsArray.filter((event) => {
      if (!event?.date) return false;
      const eventDate = new Date(event.date);
      return eventDate < todayStart;
    });
  }, [eventsArray, todayStart]);

  // Filtered and paginated events
  const filteredEvents = useMemo(() => {
    if (!eventsSearch) return eventsArray;
    const searchTerm = eventsSearch.toLowerCase();
    return eventsArray.filter((event) =>
      event?.title?.toLowerCase().includes(searchTerm)
    );
  }, [eventsArray, eventsSearch]);

  const paginatedEvents = useMemo(() => {
    const startIndex = (eventsPage - 1) * ITEMS_PER_PAGE;
    return filteredEvents.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredEvents, eventsPage]);

  const totalEventsPages = Math.ceil(filteredEvents.length / ITEMS_PER_PAGE);

  const getStatusBadge = (status) => {
    const statusStyles = {
      pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
      accepted: "bg-green-100 text-green-800 border-green-200",
      rejected: "bg-red-100 text-red-800 border-red-200",
      active: "bg-green-100 text-green-800 border-green-200",
      expired: "bg-red-100 text-red-800 border-red-200",
      upcoming: "bg-blue-100 text-blue-800 border-blue-200",
      completed: "bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-100 border-gray-200 dark:border-gray-700",
    }
    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${statusStyles[status] || statusStyles.pending
          }`}
      >
        {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </span>
    )
  }

  const PaginationControls = ({ currentPage, totalPages, onPageChange, itemName }) => {
    if (totalPages <= 1) return null
    return (
      <div className="flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 sm:px-6">
        <div className="flex justify-between flex-1 sm:hidden">
          <button
            onClick={() => onPageChange(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <button
            onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="relative ml-3 inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
        <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              Showing <span className="font-medium">{(currentPage - 1) * ITEMS_PER_PAGE + 1}</span> to{" "}
              <span className="font-medium">{Math.min(currentPage * ITEMS_PER_PAGE, filteredBids.length)}</span> of{" "}
              <span className="font-medium">{filteredBids.length}</span> {itemName}
            </p>
          </div>
          <div>
            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
              <button
                onClick={() => onPageChange(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
              {[...Array(totalPages)].map((_, index) => {
                const pageNum = index + 1
                return (
                  <button
                    key={pageNum}
                    onClick={() => onPageChange(pageNum)}
                    className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === pageNum
                      ? "z-10 bg-blue-50 border-blue-500 text-blue-600"
                      : "bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50"
                      }`}
                  >
                    {pageNum}
                  </button>
                )
              })}
              <button
                onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </nav>
          </div>
        </div>
      </div>
    )
  }

  // Get user's display name
  const getUserDisplayName = () => {
    return user?.first_name && user?.last_name
      ? `${user.first_name} ${user.last_name}`
      : user?.username || user?.email || "User"
  }

  // Get role display name
  const getRoleDisplayName = () => {
    const roleNames = {
      service_provider: "Service Provider",
      event_organizer: "Event Organizer",
      event_attendee: "Event Attendee",
      community_member: "Community Member",
    }
    return roleNames[user?.role] || "User"
  }

  if (loading) {
    return (
      <>
        {showPaymentModal && <PaymentModal onClose={() => setShowPaymentModal(false)} />}
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-100 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800 flex items-center justify-center">
          <div className="text-center">
            <div className="flex flex-col items-center justify-center min-h-screen space-y-8">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full blur-xl opacity-20 animate-pulse"></div>
                <div className="relative bg-white dark:bg-gray-800 rounded-full p-8 shadow-2xl">
                  <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-600">
                    <div className="absolute top-0 left-0 h-16 w-16 rounded-full border-4 border-transparent border-t-blue-600"></div>
                  </div>
                </div>
                <div className="space-y-4">
                  <h2 className="text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                    Loading your dashboard...
                  </h2>
                  <div className="flex items-center justify-center space-x-2">
                    <div className="h-2 w-2 bg-blue-600 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                    <div className="h-2 w-2 bg-indigo-600 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                    <div className="h-2 w-2 bg-purple-600 rounded-full animate-bounce"></div>
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 max-w-md">
                    Preparing your personalized dashboard experience with real-time data...
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    )
  }

  if (!isLoggedIn || !user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-100 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
        <div className="bg-white/90 backdrop-blur-lg rounded-3xl shadow-2xl p-10 max-w-md w-full border border-gray-200 dark:border-gray-700">
          <div className="text-center">
            <div className="mx-auto h-24 w-24 bg-gradient-to-r from-blue-100 to-indigo-100 rounded-3xl flex items-center justify-center mb-8">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-12 h-12 text-blue-600">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
            </div>
            <h3 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-6">
              Welcome Back
            </h3>
            <div className="space-y-4 mb-8">
              <p className="text-gray-700 dark:text-gray-300 text-lg leading-relaxed">
                Please sign in to access your personalized dashboard and manage your account.
              </p>
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-700 dark:to-gray-800 rounded-xl p-4">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  <span className="font-semibold">✨ Premium Features:</span> Real-time analytics, advanced filtering, and personalized insights await you.
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate("/login")}
              className="group w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-4 px-8 rounded-2xl hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-4 focus:ring-blue-200 shadow-xl hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300 font-bold text-lg"
            >
              <svg className="-ml-1 mr-3 h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 16l-4-4m0 0l4 4m-4-4v14m-4-4h14M5 16l-4-4m0 0l4 4m-4-4v14m-4-4h14"
                />
              </svg>
              Sign In to Continue
              <div className="absolute inset-0 rounded-2xl bg-white dark:bg-gray-800 opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Redirect admin users to admin dashboard
  if (user.role === "admin") {
    navigate("/admin")
    return null
  }

  const quickAction = getQuickAction()
  const roleStats = getRoleStats()
  const isServiceProvider = user?.role === "service_provider"

  // Chart data preparation
  const revenueChartData = {
    labels: analytics.monthlyTrends.map(trend => trend.month),
    datasets: [
      {
        label: 'Revenue (KES)',
        data: analytics.monthlyTrends.map(trend => trend.revenue),
        borderColor: '#8B5CF6',
        backgroundColor: 'rgba(139, 92, 246, 0.1)',
        tension: 0.4,
        fill: true
      }
    ]
  }

  const eventsChartData = {
    labels: analytics.monthlyTrends.map(trend => trend.month),
    datasets: [
      {
        label: 'Events',
        data: analytics.monthlyTrends.map(trend => trend.events),
        backgroundColor: '#10B981',
        borderRadius: 8
      }
    ]
  }

  const attendeesChartData = {
    labels: analytics.monthlyTrends.map(trend => trend.month),
    datasets: [
      {
        label: 'Attendees',
        data: analytics.monthlyTrends.map(trend => trend.attendees),
        backgroundColor: '#F59E0B',
        borderRadius: 8
      }
    ]
  }

  const eventTypeChartData = {
    labels: analytics.eventTypes.map(type => type.name),
    datasets: [
      {
        data: analytics.eventTypes.map(type => type.value),
        backgroundColor: analytics.eventTypes.map(type => type.color),
        borderWidth: 2,
        borderColor: '#fff'
      }
    ]
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: { size: 14 },
        bodyFont: { size: 12 },
        borderColor: '#fff',
        borderWidth: 1
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        }
      },
      x: {
        grid: {
          display: false
        }
      }
    }
  }

  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 15,
          font: { size: 12 }
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: { size: 14 },
        bodyFont: { size: 12 },
        borderColor: '#fff',
        borderWidth: 1
      }
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800">
      {showPaymentModal && <PaymentModal onClose={() => setShowPaymentModal(false)} />}
      
      {/* Modern Header */}
      <div className="bg-white/80 backdrop-blur-lg shadow-lg border-b border-gray-200 dark:border-gray-700/50 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl shadow-lg">
                  <svg className="h-8 w-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                </div>
                <div>
                  <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                    Dashboard
                  </h1>
                  <p className="mt-1 text-gray-600 dark:text-gray-400">
                    Welcome back, <span className="font-semibold text-blue-600">{getUserDisplayName()}</span>! 👋
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              {/* Notifications */}
              <div className="relative z-[60]">
                <button
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="relative p-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <Bell className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                  {notifications.filter(n => !n.read).length > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                      {notifications.filter(n => !n.read).length}
                    </span>
                  )}
                </button>
                
                {/* Notifications Dropdown */}
                <AnimatePresence>
                  {showNotifications && (
                    <motion.div
                      initial={{ opacity: 0, y: -10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -10, scale: 0.95 }}
                      transition={{ duration: 0.2 }}
                      className="absolute right-0 mt-2 w-80 bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-100 dark:border-gray-700 z-[70] max-h-96 overflow-y-auto"
                    >
                      <div className="p-4 border-b border-gray-100 dark:border-gray-700">
                        <div className="flex items-center justify-between">
                          <h3 className="font-semibold text-gray-900 dark:text-white">Notifications</h3>
                          <button
                            onClick={() => {
                              setNotifications(notifications.map(n => ({ ...n, read: true })))
                            }}
                            className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                          >
                            Mark all as read
                          </button>
                        </div>
                      </div>
                      
                      <div className="max-h-80 overflow-y-auto">
                        {notifications.length === 0 ? (
                          <div className="p-8 text-center">
                            <Bell className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                            <p className="text-gray-500 dark:text-gray-400 text-sm">No notifications yet</p>
                          </div>
                        ) : (
                          notifications.map((notification) => (
                            <div
                              key={notification.id}
                              className={`p-4 border-b border-gray-50 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer ${
                                !notification.read ? 'bg-blue-50' : ''
                              }`}
                            >
                              <div className="flex items-start space-x-3">
                                <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                                  !notification.read ? 'bg-blue-600' : 'bg-gray-300'
                                }`}></div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                                    {notification.title}
                                  </p>
                                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                    {notification.message}
                                  </p>
                                  <p className="text-xs text-gray-400 mt-2">
                                    {notification.time}
                                  </p>
                                </div>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              
              <div className="hidden sm:block">
                <div className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-gray-700 dark:to-gray-800 border border-amber-200 rounded-lg px-4 py-2">
                  <p className="text-xs font-semibold text-amber-800 uppercase tracking-wide">{getRoleDisplayName()}</p>
                </div>
              </div>
              <button
                onClick={quickAction.action}
                className="group relative inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-4 focus:ring-blue-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200"
              >
                <svg className="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={quickAction.icon} />
                </svg>
                {quickAction.label}
                <div className="absolute inset-0 rounded-xl bg-white dark:bg-gray-800 opacity-0 group-hover:opacity-20 transition-opacity duration-200"></div>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Advanced KPI Cards */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="grid gap-6 mb-8 md:grid-cols-2 lg:grid-cols-4"
        >
          <motion.div
            whileHover={{ scale: 1.02, y: -2 }}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 p-6 relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full -mr-16 -mt-16 opacity-10"></div>
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-xl">
                  <DollarSign className="h-6 w-6 text-white" />
                </div>
                <div className="flex items-center space-x-1">
                  {analytics.growthRate > 0 ? (
                    <TrendingUp className="h-4 w-4 text-green-500" />
                  ) : (
                    <TrendingDown className="h-4 w-4 text-red-500" />
                  )}
                  <span className={`text-sm font-medium ${analytics.growthRate > 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {analytics.growthRate > 0 ? '+' : ''}{analytics.growthRate}%
                  </span>
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Total Revenue</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white">KES {(realRevenue ?? analytics.totalRevenue).toLocaleString()}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">Last 30 days</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.02, y: -2 }}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 p-6 relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full -mr-16 -mt-16 opacity-10"></div>
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl">
                  <Calendar className="h-6 w-6 text-white" />
                </div>
                <div className="flex items-center space-x-1">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">Live</span>
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Total Events</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white">{analytics.totalEvents}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">All time</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.02, y: -2 }}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 p-6 relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-amber-500 to-orange-600 rounded-full -mr-16 -mt-16 opacity-10"></div>
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-gradient-to-r from-amber-500 to-orange-600 rounded-xl">
                  <Users className="h-6 w-6 text-white" />
                </div>
                <div className="flex items-center space-x-1">
                  <Activity className="h-4 w-4 text-blue-500" />
                  <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">Active</span>
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Total Attendees</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white">{analytics.totalAttendees.toLocaleString()}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">Across all events</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.02, y: -2 }}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 p-6 relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-full -mr-16 -mt-16 opacity-10"></div>
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-gradient-to-r from-blue-500 to-cyan-600 rounded-xl">
                  <Target className="h-6 w-6 text-white" />
                </div>
                <div className="flex items-center space-x-1">
                  <Zap className="h-4 w-4 text-yellow-500" />
                  <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">Hot</span>
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Conversion Rate</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white">
                  {(() => {
                    const totalCapacity = Object.values(events).reduce((sum, event) => sum + (event.max_attendees || 100), 0)
                    const totalAttendees = Object.values(eventAttendees).reduce((sum, count) => sum + count, 0)
                    const rate = totalCapacity > 0 ? (totalAttendees / totalCapacity) * 100 : 0
                    return `${rate.toFixed(1)}%`
                  })()}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">Registration to attendance</p>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Analytics Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
                <BarChart3 className="h-6 w-6 mr-3 text-purple-600" />
                Analytics Overview
              </h2>
              <button
                onClick={() => setExpandedSections(prev => ({ ...prev, analytics: !prev.analytics }))}
                className="ml-4 p-2 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                {expandedSections.analytics ? (
                  <ChevronUp className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                )}
              </button>
            </div>
            <div className="flex items-center space-x-3">
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                className="px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm bg-white dark:bg-gray-800"
              >
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
                <option value="90d">Last 90 days</option>
                <option value="1y">Last year</option>
              </select>
              <button
                onClick={refreshData}
                className={`p-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${refreshing ? 'animate-spin' : ''}`}
              >
                <RefreshCw className="h-4 w-4 text-gray-600 dark:text-gray-400" />
              </button>
            </div>
          </div>

          {expandedSections.analytics && (
            <div>
              <div className="flex flex-col lg:flex-row gap-6 items-stretch lg:items-start">
                {/* Revenue Chart */}
                <div className="flex-1 bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 overflow-hidden transition-all duration-300">
                  <div 
                    className="p-6 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    onClick={() => setExpandedCards(prev => ({ ...prev, revenueTrends: !prev.revenueTrends }))}
                  >
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Revenue Trends</h3>
                      <div className="flex items-center space-x-2">
                        <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                          <div className="w-3 h-3 bg-purple-600 rounded-full"></div>
                          <span>Revenue (KES)</span>
                        </div>
                        {expandedCards.revenueTrends ? (
                          <ChevronUp className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                        )}
                      </div>
                    </div>
                  </div>
                  <div className={`transition-all duration-300 ${expandedCards.revenueTrends ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0 overflow-hidden'}`}>
                    <div className="px-6 pb-6">
                      <div className="h-64">
                        <Line data={revenueChartData} options={chartOptions} />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Event Types Chart */}
                <div className="lg:w-96 bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 overflow-hidden transition-all duration-300">
                  <div 
                    className="p-6 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    onClick={() => setExpandedCards(prev => ({ ...prev, eventTypes: !prev.eventTypes }))}
                  >
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Event Types</h3>
                      <div className="flex items-center space-x-2">
                        <PieChart className="h-5 w-5 text-gray-400" />
                        {expandedCards.eventTypes ? (
                          <ChevronUp className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                        )}
                      </div>
                    </div>
                  </div>
                  <div className={`transition-all duration-300 ${expandedCards.eventTypes ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0 overflow-hidden'}`}>
                    <div className="px-6 pb-6">
                      <div className="h-64">
                        <Doughnut data={eventTypeChartData} options={pieChartOptions} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex flex-col lg:flex-row gap-6 mt-6 items-stretch lg:items-start">
                {/* Events Chart */}
                <div className="flex-1 bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 overflow-hidden transition-all duration-300">
                  <div 
                    className="p-6 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    onClick={() => setExpandedCards(prev => ({ ...prev, eventsCreated: !prev.eventsCreated }))}
                  >
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Events Created</h3>
                      <div className="flex items-center space-x-2">
                        <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                          <div className="w-3 h-3 bg-green-600 rounded-full"></div>
                          <span>Events</span>
                        </div>
                        {expandedCards.eventsCreated ? (
                          <ChevronUp className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                        )}
                      </div>
                    </div>
                  </div>
                  <div className={`transition-all duration-300 ${expandedCards.eventsCreated ? 'max-h-80 opacity-100' : 'max-h-0 opacity-0 overflow-hidden'}`}>
                    <div className="px-6 pb-6">
                      <div className="h-48">
                        <Bar data={eventsChartData} options={chartOptions} />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Attendees Chart */}
                <div className="flex-1 bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 overflow-hidden transition-all duration-300">
                  <div 
                    className="p-6 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    onClick={() => setExpandedCards(prev => ({ ...prev, attendeeGrowth: !prev.attendeeGrowth }))}
                  >
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Attendee Growth</h3>
                      <div className="flex items-center space-x-2">
                        <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                          <div className="w-3 h-3 bg-amber-600 rounded-full"></div>
                          <span>Attendees</span>
                        </div>
                        {expandedCards.attendeeGrowth ? (
                          <ChevronUp className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                        )}
                      </div>
                    </div>
                  </div>
                  <div className={`transition-all duration-300 ${expandedCards.attendeeGrowth ? 'max-h-80 opacity-100' : 'max-h-0 opacity-0 overflow-hidden'}`}>
                    <div className="px-6 pb-6">
                      <div className="h-48">
                        <Bar data={attendeesChartData} options={chartOptions} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </motion.div>

        {/* Quick Actions & Recent Activity */}
        {expandedSections.activity && (
          <div className="grid gap-6 lg:grid-cols-3">
            {/* Quick Actions */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 overflow-hidden"
            >
              <div className="px-6 py-5 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-gray-700 dark:to-gray-800 border-b border-gray-100 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center">
                    <div className="p-2 bg-green-600 rounded-lg mr-3">
                      <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </div>
                    Quick Actions
                  </h3>
                  <button
                    onClick={() => setExpandedSections(prev => ({ ...prev, activity: !prev.activity }))}
                    className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    {expandedSections.activity ? (
                      <ChevronUp className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                    )}
                  </button>
                </div>
              </div>
              <div className="p-6 space-y-3">
                <button
                  onClick={() => navigate("/my-events")}
                  className="w-full group flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900 rounded-xl hover:bg-gradient-to-r hover:from-purple-50 hover:to-indigo-50 transition-all duration-200"
                >
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300 group-hover:text-purple-600">Create Event</span>
                  <svg className="h-4 w-4 text-gray-400 group-hover:text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                </button>
                <button
                  onClick={() => navigate("/my-events")}
                  className="w-full group flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all duration-200"
                >
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300 group-hover:text-blue-600">View Events</span>
                  <svg className="h-4 w-4 text-gray-400 group-hover:text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </button>
                <button
                  onClick={() => navigate("/profile")}
                  className="w-full group flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all duration-200"
                >
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300 group-hover:text-blue-600">View Profile</span>
                  <svg className="h-4 w-4 text-gray-400 group-hover:text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
                <button
                  onClick={() => navigate("/settings")}
                  className="w-full group flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900 rounded-xl hover:bg-gradient-to-r hover:from-purple-50 hover:to-pink-50 transition-all duration-200"
                >
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300 group-hover:text-purple-600">Settings</span>
                  <svg className="h-4 w-4 text-gray-400 group-hover:text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c-.94 1.543-.826 3.31.826 2.37a1.724 1.724 0 00-2.572 1.065c-.426-1.756-2.924-1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c.94-1.543.826-3.31-.826-2.37zm1.06 2.572a1.724 1.724 0 012.573-1.066c1.543-.94 3.31.826 2.37 2.37.996.608 2.296.07 2.572-1.065z" />
                  </svg>
                </button>
                <button
                  onClick={() => navigate("/help")}
                  className="w-full group flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900 rounded-xl hover:bg-gradient-to-r hover:from-green-50 hover:to-emerald-50 transition-all duration-200"
                >
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300 group-hover:text-green-600">Help & Support</span>
                  <svg className="h-4 w-4 text-gray-400 group-hover:text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </button>
              </div>
            </motion.div>

            {/* Recent Activity */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 p-6"
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Recent Activity</h3>
                <Activity className="h-5 w-5 text-blue-500" />
              </div>
              <div className="space-y-4">
                {analytics.recentActivity.map((activity, index) => {
                  const Icon = activity.icon
                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: 0.4 + index * 0.1 }}
                      className="flex items-center space-x-4 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <div className="flex-shrink-0">
                        <div className="p-2 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-700 dark:to-gray-800 rounded-lg">
                          <Icon className="h-4 w-4 text-blue-600" />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{activity.message}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{activity.time}</p>
                      </div>
                    </motion.div>
                  )
                })}
              </div>
            </motion.div>
          </div>
        )}

        {/* Calendar View Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="mb-8 mt-12"
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
                <Calendar className="h-6 w-6 mr-3 text-indigo-600" />
                Upcoming Events
              </h2>
              <button
                onClick={() => setExpandedSections(prev => ({ ...prev, events: !prev.events }))}
                className="ml-4 p-2 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                {expandedSections.events ? (
                  <ChevronUp className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                )}
              </button>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setEventsView('all')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  eventsView === 'all' 
                    ? 'bg-indigo-600 text-white' 
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:bg-gray-50'
                }`}
              >
                All Events
              </button>
              <button
                onClick={() => setEventsView('organizing')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  eventsView === 'organizing' 
                    ? 'bg-indigo-600 text-white' 
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:bg-gray-50'
                }`}
              >
                Organizing
              </button>
              <button
                onClick={() => setEventsView('attending')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  eventsView === 'attending' 
                    ? 'bg-indigo-600 text-white' 
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:bg-gray-50'
                }`}
              >
                Attending
              </button>
            </div>
          </div>

          {expandedSections.events && (
            <div className="grid gap-6 lg:grid-cols-3">
              {/* Calendar Widget */}
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Calendar</h3>
                  <Clock className="h-5 w-5 text-gray-400" />
                </div>
                <div className="space-y-3">
                  {eventsToDisplay.slice(0, 5).map((event, index) => {
                    const eventDate = new Date(event.date)
                    const isEventToday = isToday(eventDate)
                    const isEventTomorrow = isTomorrow(eventDate)
                    
                    return (
                      <div
                        key={event.id}
                        className="flex items-center space-x-3 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                        onClick={() => navigate(`/events/${event.slug || event.id}`)}
                      >
                        <div className={`flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center ${
                          isEventToday ? 'bg-indigo-600' : isEventTomorrow ? 'bg-purple-600' : 'bg-gray-100'
                        }`}>
                          <div className={`text-center ${isEventToday || isEventTomorrow ? 'text-white' : 'text-gray-600 dark:text-gray-400'}`}>
                            <div className="text-xs font-medium">
                              {format(eventDate, 'MMM')}
                            </div>
                            <div className="text-lg font-bold">
                              {format(eventDate, 'd')}
                            </div>
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {event.title || "Untitled Event"}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {isEventToday ? 'Today' : isEventTomorrow ? 'Tomorrow' : format(eventDate, 'MMM d, yyyy')}
                          </p>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>

              {/* Events List */}
              <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Recent Events</h3>
                  <div className="flex items-center space-x-2">
                    <Search className="h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search events..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="px-3 py-1.5 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                </div>
                
                <div className="space-y-4">
                  {eventsToDisplay
                    .filter(event => 
                      searchQuery === '' || 
                      event.title?.toLowerCase().includes(searchQuery.toLowerCase())
                    )
                    .slice(0, 6)
                    .map((event) => (
                      <motion.div
                        key={event.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                        className="flex items-center space-x-4 p-4 rounded-xl border border-gray-100 dark:border-gray-700 hover:border-indigo-200 hover:shadow-md transition-all cursor-pointer"
                        onClick={() => navigate(`/events/${event.slug || event.id}`)}
                      >
                        <div className="flex-shrink-0">
                          <div className="w-12 h-12 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
                            <Calendar className="h-6 w-6 text-white" />
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                              {event.title || "Untitled Event"}
                            </p>
                            <div className="flex items-center space-x-2">
                              {event.isOrganizer !== undefined && (
                                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                  event.isOrganizer
                                    ? 'bg-purple-100 text-purple-700'
                                    : 'bg-green-100 text-green-700'
                                }`}>
                                  {event.isOrganizer ? 'Organizing' : 'Attending'}
                                </span>
                              )}
                              {event.is_paid_event && (
                                <span className="px-2 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-700">
                                  Paid
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400">
                            <span className="flex items-center">
                              <Clock className="h-3 w-3 mr-1" />
                              {format(new Date(event.date), 'MMM d, yyyy')}
                            </span>
                            {event.location && (
                              <span className="flex items-center">
                                <MapPin className="h-3 w-3 mr-1" />
                                {event.location.address || event.location}
                              </span>
                            )}
                            <span className="flex items-center">
                              <Users className="h-3 w-3 mr-1" />
                              {eventAttendees[event.id] || 0} attendees
                            </span>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                </div>
              </div>
            </div>
          )}
        </motion.div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Service Provider Content */}
          {user.role === "service_provider" && (
            <div className="lg:col-span-2">
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
                <div className="px-6 py-5 border-b border-gray-100 dark:border-gray-700 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-700 dark:to-gray-800">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center">
                        <div className="p-2 bg-blue-600 rounded-lg mr-3">
                          <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                        </div>
                        Available Opportunities
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                        <span className="font-semibold text-blue-600">{filteredBids.length}</span> of <span className="font-medium">{bids.length}</span> opportunities
                      </p>
                    </div>
                    <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
                      <div className="relative">
                        <input
                          type="text"
                          placeholder="Search opportunities..."
                          value={bidsSearch}
                          onChange={(e) => setBidsSearch(e.target.value)}
                          className="pl-10 pr-4 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm shadow-sm bg-gray-50/50 backdrop-blur-sm"
                        />
                        <svg
                          className="absolute left-3 top-3 h-4 w-4 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                          />
                        </svg>
                      </div>
                      <select
                        value={bidsFilter}
                        onChange={(e) => setBidsFilter(e.target.value)}
                        className="px-4 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm shadow-sm appearance-none bg-white/80 backdrop-blur-sm"
                      >
                        <option value="all">All Status</option>
                        <option value="pending">Open</option>
                        <option value="accepted">Won</option>
                        <option value="rejected">Not Selected</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  {paginatedBids.length > 0 ? (
                    <div className="space-y-4">
                      {paginatedBids.map((bid) => (
                        <div
                          key={bid.id}
                          onMouseEnter={() => setActiveBid(bid.id)}
                          onMouseLeave={() => setActiveBid(null)}
                          onClick={() => {
                            if (bid.event?.slug) {
                              navigate(`/events/${bid.event.slug}`)
                            }
                          }}
                          className={`group relative p-6 rounded-xl border transition-all duration-300 cursor-pointer overflow-hidden ${
                            activeBid === bid.id
                              ? "border-blue-500 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-700 dark:to-gray-800 shadow-xl transform scale-[1.02]"
                              : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-blue-300 hover:shadow-lg hover:transform hover:scale-[1.01]"
                          }`}
                        >
                          {/* Hover gradient overlay */}
                          <div className="absolute inset-0 bg-gradient-to-r from-blue-600/3 to-indigo-600/3 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl"></div>
                          
                          <div className="relative z-10">
                            <div className="flex items-start justify-between mb-4">
                              <div className="flex-1">
                                <div className="flex items-center justify-between mb-3">
                                  <h4 className="font-bold text-gray-900 dark:text-white text-lg group-hover:text-blue-600 transition-colors">
                                    {bid.title || "Untitled Opportunity"}
                                  </h4>
                                  <div className="flex items-center space-x-2">
                                    {getStatusBadge(bid.status)}
                                    {bid.is_urgent && (
                                      <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-semibold rounded-full border border-red-200">
                                        Urgent
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 flex items-center">
                                  <svg className="h-4 w-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                  </svg>
                                  {bid.event?.title ? `Event: ${bid.event?.title}` : "No event info"}
                                </p>
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center space-x-3">
                                    <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-2 rounded-xl">
                                      <p className="text-xl font-bold">
                                        KSh {(bid.price || 0).toLocaleString()}
                                      </p>
                                    </div>
                                    {bid.deadline && (
                                      <div className="text-right">
                                        <p className="text-xs text-gray-500 dark:text-gray-400">Deadline</p>
                                        <p className="text-sm font-semibold text-red-600">
                                          {new Date(bid.deadline).toLocaleDateString()}
                                        </p>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="flex-shrink-0 ml-4">
                                <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg group-hover:bg-blue-100 transition-colors duration-200">
                                  <svg
                                    className="h-5 w-5 text-gray-500 dark:text-gray-400 group-hover:text-blue-600 transition-colors duration-200"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                  </svg>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                Posted {new Date(bid.created_at || Date.now()).toLocaleDateString()}
                              </p>
                              {bid.proposals_count && (
                                <div className="flex items-center space-x-1">
                                  <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                                  </svg>
                                  <span className="text-xs text-gray-500 dark:text-gray-400">{bid.proposals_count} proposals</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-16">
                      <div className="max-w-md mx-auto">
                        <div className="mx-auto h-20 w-20 bg-gradient-to-r from-gray-100 to-gray-200 rounded-2xl flex items-center justify-center mb-6">
                          <svg className="h-10 w-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={1.5}
                              d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2H5a2 2 0 00-2 2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                            />
                          </svg>
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                          {bidsSearch || bidsFilter !== "all"
                            ? "No matching opportunities found"
                            : "No opportunities yet"}
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400 mb-8 text-lg">
                          {bidsSearch || bidsFilter !== "all"
                            ? "Try adjusting your search or filter criteria"
                            : "Check back soon for new opportunities to bid on."}
                        </p>
                        {!bidsSearch && bidsFilter === "all" && (
                          <button
                            onClick={() => navigate("/bids")}
                            className="group inline-flex items-center px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-4 focus:ring-blue-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200"
                          >
                            <svg className="-ml-1 mr-3 h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                              />
                            </svg>
                            Browse Opportunities
                            <div className="absolute inset-0 rounded-xl bg-white dark:bg-gray-800 opacity-0 group-hover:opacity-20 transition-opacity duration-200"></div>
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                {totalBidsPages > 1 && (
                  <PaginationControls
                    currentPage={bidsPage}
                    totalPages={totalBidsPages}
                    onPageChange={setBidsPage}
                    itemName="opportunities"
                  />
                )}
              </div>
            </div>
          )}

          {/* Event Organizer Content */}
          {user.role === "event_organizer" && (
            <div className="lg:col-span-2">
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
                <div className="px-6 py-5 border-b border-gray-100 dark:border-gray-700 bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-gray-700 dark:to-gray-800">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center">
                        <div className="p-2 bg-purple-600 rounded-lg mr-3">
                          <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v7a2 2 0 002 2z" />
                          </svg>
                        </div>
                        My Events
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                        <span className="font-semibold text-purple-600">{Object.values(events).filter(e => e.isOrganizer).length}</span> organizing • <span className="font-medium text-blue-600">{Object.values(events).filter(e => !e.isOrganizer).length}</span> attending
                      </p>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setEventsView('all')}
                        className={`px-4 py-2.5 text-sm font-semibold rounded-xl transition-all duration-200 ${eventsView === 'all'
                          ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-lg transform scale-105'
                          : 'text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 hover:border-gray-300 dark:hover:border-gray-500'
                          }`}
                      >
                        All Events
                      </button>
                      <button
                        onClick={() => setEventsView('organizing')}
                        className={`px-4 py-2.5 text-sm font-semibold rounded-xl transition-all duration-200 ${eventsView === 'organizing'
                          ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-lg transform scale-105'
                          : 'text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 hover:border-gray-300 dark:hover:border-gray-500'
                          }`}
                      >
                        Organizing
                      </button>
                      <button
                        onClick={() => setEventsView('attending')}
                        className={`px-4 py-2.5 text-sm font-semibold rounded-xl transition-all duration-200 ${eventsView === 'attending'
                          ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-lg transform scale-105'
                          : 'text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 hover:border-gray-300 dark:hover:border-gray-500'
                          }`}
                      >
                        Attending
                      </button>
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  {eventsToDisplay.length > 0 ? (
                    <div className="space-y-5">
                      {eventsToDisplay
                        .sort((a, b) => {
                          // Sort by date, most recent first
                          const dateA = new Date(a.date || a.created_at || 0);
                          const dateB = new Date(b.date || b.created_at || 0);
                          return dateB - dateA;
                        })
                        .map((event) => (
                          <div
                            key={event.id}
                            onClick={() => navigate(`/events/${event.slug || event.id}`)}
                            className="group relative p-6 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-purple-300 hover:shadow-xl cursor-pointer transition-all duration-300 overflow-hidden"
                          >
                            {/* Hover gradient overlay */}
                            <div className="absolute inset-0 bg-gradient-to-r from-purple-600/3 to-indigo-600/3 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl"></div>
                            
                              <div className="relative z-10">
                                <div className="flex items-start justify-between mb-4">
                                  <div className="flex-1">
                                    <div className="flex items-center justify-between mb-3">
                                      <h4 className="font-bold text-gray-900 dark:text-white text-lg group-hover:text-purple-600 transition-colors">
                                        {event.title || "Untitled Event"}
                                      </h4>
                                      <div className="flex items-center space-x-2">
                                        {event.visibility && getStatusBadge(event.visibility)}
                                        {event.is_featured && (
                                          <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs font-semibold rounded-full border border-amber-200">
                                            Featured
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    <div className="space-y-3 mb-4">
                                      <div className="flex items-center space-x-3">
                                        {event.isOrganizer !== undefined && (
                                          <span className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${event.isOrganizer
                                            ? 'bg-gradient-to-r from-purple-100 to-indigo-100 text-purple-900 border border-purple-200'
                                            : 'bg-gradient-to-r from-green-100 to-emerald-100 text-green-900 border border-green-200'
                                            }`}>
                                            {event.isOrganizer ? 'Organizing' : 'Attending'}
                                          </span>
                                        )}
                                        {event.is_paid_event && (
                                          <span className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-gradient-to-r from-amber-100 to-orange-100 text-amber-800 border border-amber-200">
                                            Paid Event
                                          </span>
                                        )}
                                      </div>
                                      {event.is_paid_event ? (
                                        <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-gray-700 dark:to-gray-800 rounded-xl p-3">
                                          <p className="text-sm font-bold text-green-700">
                                            <span className="text-xs uppercase tracking-wider">Ticket Price:</span> KES {(event.ticket_price || 0).toLocaleString()}
                                          </p>
                                        </div>
                                      ) : event.location ? (
                                        <div className="bg-gray-50 dark:bg-gray-900 rounded-xl p-3">
                                          <p className="text-sm text-gray-700 dark:text-gray-300 flex items-center">
                                            <svg className="h-4 w-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-1.414 0l-5.657-5.657a1 998 1.998 0 010-2.828l5.657-5.657a1.998 1.998 0 012.828 0L20.9 13.414a1.998 1.998 0 010 2.828l-5.657 5.657a1.998 1.998 0 01-2.828 0z" />
                                            </svg>
                                            {event.location.address || event.location}
                                          </p>
                                        </div>
                                      ) : null}
                                    </div>
                                  </div>
                                    <div className="flex-shrink-0 ml-4">
                                      <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-xl group-hover:bg-purple-100 transition-colors duration-200">
                                        <svg
                                          className="h-5 w-5 text-gray-500 dark:text-gray-400 group-hover:text-purple-600 transition-colors duration-200"
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                      </svg>
                                    </div>
                                  </div>
                                  <div className="flex items-center justify-between pt-4 border-t border-gray-100 dark:border-gray-700">
                                    <div className="flex items-center space-x-4">
                                      {event.isOrganizer && (
                                        <div className="flex items-center space-x-2 min-w-[120px]">
                                          {loadingEvents[event.id] ? (
                                            <>
                                              <div className="h-4 w-4 border-2 border-purple-200 border-t-purple-600 rounded-full animate-spin"></div>
                                              <span className="text-sm text-gray-500 dark:text-gray-400">Loading...</span>
                                            </>
                                          ) : (
                                            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-700 dark:to-gray-800 rounded-xl px-3 py-2">
                                              <span className="text-lg font-bold text-blue-900">
                                                {eventAttendees[event.id] || 0} {eventAttendees[event.id] === 1 ? 'Attendee' : 'Attendees'}
                                              </span>
                                            </div>
                                          )}
                                        </div>
                                      )}
                                      <div className="text-right">
                                        <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Event Date</p>
                                        <p className="text-sm font-bold text-gray-900 dark:text-white">
                                          {new Date(event.date).toLocaleDateString('en-US', { 
                                            weekday: 'short', 
                                            month: 'short', 
                                            day: 'numeric' 
                                          })}
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>                            
                        ))}
                    </div>
                  ) : (
                    <div className="text-center py-16">
                      <div className="max-w-md mx-auto">
                        <div className="mx-auto h-24 w-24 bg-gradient-to-r from-purple-100 to-indigo-100 rounded-3xl flex items-center justify-center mb-8">
                          <svg className="h-12 w-12 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={1.5}
                              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                            />
                          </svg>
                        </div>
                        <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">No events yet</h3>
                        <p className="text-gray-600 dark:text-gray-400 mb-8 text-lg leading-relaxed">
                          Ready to create your first amazing event? Let's get started!
                        </p>
                        <button
                          onClick={() => navigate("/my-events")}
                          className="group inline-flex items-center px-10 py-4 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-bold rounded-2xl hover:from-purple-700 hover:to-indigo-700 focus:outline-none focus:ring-4 focus:ring-purple-200 shadow-2xl hover:shadow-3xl transform hover:-translate-y-1 transition-all duration-300"
                        >
                          <svg className="-ml-1 mr-3 h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                            />
                          </svg>
                          Create Your First Event
                          <div className="absolute inset-0 rounded-2xl bg-white dark:bg-gray-800 opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {totalEventsPages > 1 && (
                  <PaginationControls
                    currentPage={eventsPage}
                    totalPages={totalEventsPages}
                    onPageChange={setEventsPage}
                    itemName="events"
                  />
                )}
              </div>
            </div>
          )}

          {/* Modern Sidebar */}
          <div className="space-y-6">
            {/* Account Information Card */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
              <div className="px-6 py-5 bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-gray-700 dark:to-gray-800 border-b border-gray-100 dark:border-gray-700">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center">
                  <div className="p-2 bg-purple-600 rounded-lg mr-3">
                    <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  Account Information
                </h3>
              </div>
              <div className="p-6 space-y-5">
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 rounded-xl p-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Name</span>
                    <span className="text-sm font-bold text-gray-900 dark:text-white">{getUserDisplayName()}</span>
                  </div>
                </div>
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-700 dark:to-gray-800 rounded-xl p-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Role</span>
                    <span className="inline-flex items-center px-3 py-1.5 rounded-xl text-xs font-bold bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg">
                      {getRoleDisplayName()}
                    </span>
                  </div>
                </div>
                
                {/* Subscription Status */}
                {subscription && (
                  <div className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-gray-700 dark:to-gray-800 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Subscription</span>
                      <div className="flex items-center space-x-1">
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                        <span className="text-xs font-medium text-green-700">Active</span>
                      </div>
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      {subscription.plan?.name} • Expires {subscription.end_date ? new Date(subscription.end_date).toLocaleDateString() : 'No expiration date'}
                    </p>
                  </div>
                )}
              </div>
            </div>            
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
